/**
 * VÉRAULT Portal — Advisor: Client Forms Panel (Forms Phase 1)
 *
 * Lives inside ClientProfile under the "Forms" tab. Two surfaces:
 *
 *   1. Onboarding status board — at-a-glance counts of what's been sent /
 *      completed / outstanding for THIS client, drawn from the packets list.
 *   2. Packets list — every onboarding packet ever sent, with quick actions
 *      (copy link, resend, cancel) and per-section completion progress.
 *
 * The "Send packet" drawer is the heart of the experience: select sections
 * (with presets like "Agreement bundle" and "Full intake"), generate a magic
 * link with 14-day default expiry, and either copy the link or send via
 * Concierge (placeholder for now — sends are mocked).
 */
import React from "react";
import { Link } from "react-router-dom";
import {
  Plus,
  Send,
  Copy,
  RefreshCw,
  XCircle,
  CheckCircle2,
  Clock,
  AlertCircle,
  ScrollText,
  Loader2,
  X,
  ChevronDown,
  ChevronUp,
  Eye,
  Compass,
  Layers,
  Wand2,
  Sparkles,
} from "lucide-react";
import {
  Surface,
  Heading,
  EditorialRule,
  Empty,
  Skeleton,
  PortalButton,
  StatusTag,
  api,
  fmt,
} from "../../../index";
import FormPreviewModal from "../../../components/FormPreviewModal";

const STATUS_TONE = {
  drafted: "neutral",
  sent: "gold",
  in_progress: "gold",
  completed: "sage",
  expired: "terracotta",
  cancelled: "terracotta",
};

const STATUS_LABEL = {
  drafted: "Drafted",
  sent: "Awaiting client",
  in_progress: "In progress",
  completed: "Completed",
  expired: "Expired",
  cancelled: "Cancelled",
};

const CATEGORY_LABEL = {
  agreement: "Agreement bundle",
  onboarding: "Onboarding",
  preferences: "Preferences",
  logistics: "Logistics",
  post_trip: "Post-trip",
};

const PRESETS = [
  {
    id: "agreement_bundle",
    label: "Agreement bundle",
    hint: "Travel Planning Agreement · T&C · Planning Fee",
    slugs: ["travel_planning_agreement", "terms_and_conditions", "planning_fee"],
  },
  {
    id: "core_intake",
    label: "Core intake",
    hint: "Client info · Travelers · Passports · Preferences",
    slugs: ["basic_client_info", "traveler_details", "passport_id", "travel_preferences"],
  },
  {
    id: "everything",
    label: "Everything available",
    hint: "Agreement + full intake (all Phase 1 sections)",
    slugs: [
      "travel_planning_agreement",
      "terms_and_conditions",
      "planning_fee",
      "basic_client_info",
      "traveler_details",
      "passport_id",
      "travel_preferences",
    ],
  },
];

function StatusBoard({ packets }) {
  const totals = packets.reduce(
    (acc, p) => {
      const key = p.status === "in_progress" ? "in_progress" : p.status;
      if (acc[key] !== undefined) acc[key] += 1;
      return acc;
    },
    { sent: 0, in_progress: 0, completed: 0, expired: 0, cancelled: 0 },
  );
  const cells = [
    { label: "Awaiting client", count: totals.sent, icon: Clock, tone: "gold" },
    { label: "In progress", count: totals.in_progress, icon: Loader2, tone: "gold" },
    { label: "Completed", count: totals.completed, icon: CheckCircle2, tone: "sage" },
    { label: "Stale / expired", count: totals.expired + totals.cancelled, icon: AlertCircle, tone: "terracotta" },
  ];
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="forms-status-board">
      {cells.map((c) => {
        const Icon = c.icon;
        return (
          <Surface key={c.label} variant="paper" padded={false} className="!p-4">
            <div className="flex items-center gap-2.5">
              <Icon className="size-4 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
                {c.label}
              </p>
            </div>
            <p className="font-serif text-3xl mt-2 tabular-nums">{c.count}</p>
          </Surface>
        );
      })}
    </div>
  );
}

function SendPacketDrawer({ clientId, sections, onClose, onSent }) {
  const [selected, setSelected] = React.useState(() => new Set(PRESETS[0].slugs));
  const [expiresInDays, setExpiresInDays] = React.useState(14);
  const [planningFee, setPlanningFee] = React.useState(1000);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [previewOpen, setPreviewOpen] = React.useState(false);

  // Group sections by category, sorted by phase
  const grouped = React.useMemo(() => {
    const g = {};
    sections.forEach((s) => {
      if (!g[s.category]) g[s.category] = [];
      g[s.category].push(s);
    });
    return g;
  }, [sections]);

  const toggle = (slug) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });

  const applyPreset = (preset) => setSelected(new Set(preset.slugs));

  const handleSend = async () => {
    if (selected.size === 0) {
      setError("Select at least one section.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        client_id: clientId,
        section_slugs: Array.from(selected),
        expires_in_days: expiresInDays,
        planning_fee_amount: planningFee,
      };
      const { data: packet } = await api.post("/forms/packets", payload);
      onSent(packet);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Send failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/30 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="forms-send-drawer"
    >
      <div className="w-full max-w-xl bg-[hsl(var(--background))] shadow-2xl overflow-y-auto">
        <div className="sticky top-0 bg-[hsl(var(--background))] z-10 px-6 py-4 border-b border-[rgba(0,0,0,0.06)] flex items-center justify-between">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
              Onboarding
            </p>
            <h2 className="font-serif text-xl mt-0.5">Send an onboarding packet</h2>
          </div>
          <button
            onClick={onClose}
            className="size-8 rounded inline-flex items-center justify-center text-[hsl(var(--foreground-muted))] hover:bg-[hsl(var(--background-paper))]"
            data-testid="forms-send-close"
            aria-label="Close"
          >
            <X className="size-4" strokeWidth={1.5} />
          </button>
        </div>

        <div className="px-6 py-6 space-y-6">
          {error && (
            <div className="px-3 py-2 rounded bg-[#FCEAE0] border border-[#F0C8A8] text-[13px] text-[#8B4513]">
              {error}
            </div>
          )}

          <div>
            <Heading level="eyebrow">Quick presets</Heading>
            <div className="mt-3 space-y-2">
              {PRESETS.map((p) => (
                <button
                  key={p.id}
                  onClick={() => applyPreset(p)}
                  className="w-full text-left px-4 py-3 rounded-md bg-[hsl(var(--background-paper))]/70 hover:bg-[hsl(var(--background-paper))] border border-[rgba(0,0,0,0.05)] transition-colors"
                  data-testid={`forms-preset-${p.id}`}
                >
                  <p className="font-body text-[14px]">{p.label}</p>
                  <p className="font-accent italic text-[12px] text-[hsl(var(--foreground-muted))] mt-0.5">
                    {p.hint}
                  </p>
                </button>
              ))}
            </div>
          </div>

          <EditorialRule label="Or choose sections individually" />

          <div className="space-y-5" data-testid="forms-section-picker">
            {["agreement", "onboarding", "preferences", "logistics", "post_trip"].map((cat) => {
              const items = grouped[cat] || [];
              if (items.length === 0) return null;
              return (
                <div key={cat}>
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] mb-2">
                    {CATEGORY_LABEL[cat]}
                  </p>
                  <ul className="space-y-1.5">
                    {items.map((s) => {
                      const isPhase2 = s.phase === 2;
                      const checked = selected.has(s.slug);
                      return (
                        <li key={s.slug}>
                          <label
                            className={`flex items-start gap-3 px-3 py-2 rounded-md transition-colors cursor-pointer ${
                              isPhase2
                                ? "bg-transparent opacity-50 cursor-not-allowed"
                                : checked
                                  ? "bg-[hsl(var(--accent-gold))]/10"
                                  : "hover:bg-[hsl(var(--background-paper))]/70"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={isPhase2}
                              onChange={() => toggle(s.slug)}
                              className="mt-0.5 accent-[hsl(var(--accent-gold))]"
                              data-testid={`forms-section-${s.slug}`}
                            />
                            <div className="min-w-0 flex-1">
                              <p className="font-body text-[13.5px]">{s.name}</p>
                              <p className="font-accent italic text-[12px] text-[hsl(var(--foreground-muted))] mt-0.5">
                                {s.description}
                              </p>
                            </div>
                            {isPhase2 && (
                              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] mt-0.5">
                                Coming
                              </span>
                            )}
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>

          <EditorialRule label="Send settings" />
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] mb-1.5">
                Link expires in (days)
              </p>
              <input
                type="number"
                min="1"
                max="90"
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(parseInt(e.target.value) || 14)}
                className="w-full px-3 py-2 text-[14px] bg-[hsl(var(--background-paper))] border border-transparent rounded-md outline-none focus:border-[rgba(0,0,0,0.12)] focus:bg-white"
                data-testid="forms-send-expires"
              />
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] mb-1.5">
                Planning fee ($)
              </p>
              <input
                type="number"
                min="0"
                step="50"
                value={planningFee}
                onChange={(e) => setPlanningFee(parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 text-[14px] bg-[hsl(var(--background-paper))] border border-transparent rounded-md outline-none focus:border-[rgba(0,0,0,0.12)] focus:bg-white"
                data-testid="forms-send-planning-fee"
              />
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-[hsl(var(--background))] border-t border-[rgba(0,0,0,0.06)] px-6 py-4 flex items-center justify-between gap-3">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
            {selected.size} section{selected.size === 1 ? "" : "s"} selected
          </p>
          <div className="flex items-center gap-2">
            <PortalButton variant="ghost" size="sm" onClick={onClose} disabled={saving}>
              Cancel
            </PortalButton>
            <PortalButton
              variant="ghost"
              size="sm"
              icon={<Eye className="size-3.5" />}
              onClick={() => setPreviewOpen(true)}
              disabled={saving || selected.size === 0}
              data-testid="forms-send-preview"
            >
              Preview
            </PortalButton>
            <PortalButton
              variant="primary"
              size="sm"
              icon={saving ? <Loader2 className="size-3.5 animate-spin" /> : <Send className="size-3.5" />}
              onClick={handleSend}
              disabled={saving || selected.size === 0}
              data-testid="forms-send-confirm"
            >
              {saving ? "Generating link" : "Generate magic link"}
            </PortalButton>
          </div>
        </div>
      </div>

      <FormPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        sections={sections}
        selectedSlugs={Array.from(selected)}
      />
    </div>
  );
}

function PacketSentToast({ packet, onClose }) {
  const [copied, setCopied] = React.useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(packet.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      window.prompt("Copy this link:", packet.url);
    }
  };
  return (
    <div
      className="fixed bottom-6 right-6 max-w-md bg-[hsl(var(--background))] shadow-2xl border border-[rgba(0,0,0,0.08)] rounded-lg z-50 overflow-hidden"
      data-testid="forms-sent-toast"
    >
      <div className="px-5 py-4 border-l-4 border-[hsl(var(--accent-gold))]">
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))]">
          Magic link generated
        </p>
        <p className="font-body text-[13.5px] mt-1.5 leading-relaxed">
          {packet.section_slugs.length} section{packet.section_slugs.length === 1 ? "" : "s"} ·
          expires in {Math.round((new Date(packet.expires_at) - new Date()) / (1000 * 60 * 60 * 24))} days.
        </p>
        <div className="mt-3 flex items-center gap-2">
          <input
            readOnly
            value={packet.url}
            className="flex-1 px-2 py-1.5 text-[12px] font-mono bg-[hsl(var(--background-paper))] border border-transparent rounded outline-none"
            onClick={(e) => e.target.select()}
            data-testid="forms-sent-link"
          />
          <PortalButton
            variant={copied ? "primary" : "ghost"}
            size="sm"
            icon={copied ? <CheckCircle2 className="size-3.5" /> : <Copy className="size-3.5" />}
            onClick={copy}
            data-testid="forms-sent-copy"
          >
            {copied ? "Copied" : "Copy"}
          </PortalButton>
        </div>
      </div>
      <button
        onClick={onClose}
        className="absolute top-2 right-2 size-6 rounded inline-flex items-center justify-center text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
        data-testid="forms-sent-close"
        aria-label="Close"
      >
        <X className="size-3.5" strokeWidth={1.5} />
      </button>
    </div>
  );
}

function PacketCard({ packet, sectionsByslug, onAction, busyId }) {
  const [expanded, setExpanded] = React.useState(false);
  const [autoJourney, setAutoJourney] = React.useState(null);
  const [aiSummary, setAiSummary] = React.useState(packet.ai_summary || null);
  const [summarizing, setSummarizing] = React.useState(false);
  const tone = STATUS_TONE[packet.status] || "neutral";
  const label = STATUS_LABEL[packet.status] || packet.status;
  const sections = packet.section_slugs || [];
  const completed = new Set(packet.completed_section_slugs || []);
  const missingCount = sections.length - completed.size;
  const expiresInDays = packet.expires_at
    ? Math.round((new Date(packet.expires_at) - new Date()) / (1000 * 60 * 60 * 24))
    : null;
  const isPostTrip = packet.kind === "post_trip_feedback" || sections.includes("post_trip_feedback");
  const canSummarize = completed.size > 0;

  const summarize = async () => {
    setSummarizing(true);
    try {
      const { data } = await api.post(`/forms/packets/${packet.id}/summarize`);
      setAiSummary(data?.summary || data);
    } catch (e) {
      window.alert(e?.response?.data?.detail || "AI summary unavailable right now.");
    } finally {
      setSummarizing(false);
    }
  };

  // Probe the auto-created draft journey (hybrid trigger) if this packet
  // has produced one. Silent failure — pure visibility affordance.
  React.useEffect(() => {
    let alive = true;
    const hasStarted = completed.size > 0 || packet.verified_at;
    if (!hasStarted) return undefined;
    api
      .get(`/forms/packets/${packet.id}/journey`)
      .then(({ data }) => {
        if (alive && data?.trip_id) setAutoJourney(data);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [packet.id, completed.size, packet.verified_at]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(packet.url);
    } catch {
      window.prompt("Copy this link:", packet.url);
    }
  };
  return (
    <Surface variant="paper" padded={false} className="overflow-hidden" data-testid={`packet-card-${packet.id}`}>
      <div className="px-5 py-4 space-y-2">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusTag tone={tone}>{label}</StatusTag>
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
              {sections.length} section{sections.length === 1 ? "" : "s"} · {completed.size}/{sections.length} done
            </span>
            {expiresInDays !== null && packet.status !== "completed" && packet.status !== "cancelled" && (
              <span className={`font-mono text-[10px] uppercase tracking-[0.18em] ${
                expiresInDays <= 3 ? "text-[hsl(var(--accent-terracotta))]" : "text-[hsl(var(--foreground-muted))]"
              }`}>
                {expiresInDays <= 0 ? "Expired" : `${expiresInDays}d left`}
              </span>
            )}
            {autoJourney?.trip_id && (
              <Link
                to={`/portal/advisor/journeys/${autoJourney.trip_id}`}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-[0.18em] bg-[hsl(var(--accent-gold))]/10 text-[hsl(var(--accent-gold))] hover:bg-[hsl(var(--accent-gold))]/20"
                data-testid={`packet-auto-journey-${packet.id}`}
                title={`Auto-experience · ${autoJourney.status || "draft"}`}
              >
                <Compass className="size-3" strokeWidth={1.5} />
                {autoJourney.status === "active" ? "Experience active" : "Draft experience"}
              </Link>
            )}
          </div>
          <button
            onClick={() => setExpanded((x) => !x)}
            className="text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.18em]"
            data-testid={`packet-toggle-${packet.id}`}
          >
            {expanded ? "Hide" : "Details"}
            {expanded ? <ChevronUp className="size-3" strokeWidth={1.5} /> : <ChevronDown className="size-3" strokeWidth={1.5} />}
          </button>
        </div>
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
          Sent {fmt.formatDate(packet.sent_at)}
          {packet.verified_at && ` · Verified ${fmt.formatDate(packet.verified_at)}`}
          {packet.last_accessed_at && ` · Last opened ${fmt.formatDate(packet.last_accessed_at)}`}
        </p>
      </div>
      {expanded && (
        <div className="px-5 pb-4 space-y-3 border-t border-[rgba(0,0,0,0.05)] pt-3">
          <ul className="space-y-1.5" data-testid={`packet-sections-${packet.id}`}>
            {sections.map((slug) => {
              const sec = sectionsByslug[slug];
              const done = completed.has(slug);
              return (
                <li key={slug} className="flex items-center gap-2.5">
                  {done ? (
                    <CheckCircle2 className="size-3.5 text-[hsl(var(--accent-sage,170_18%_42%))]" strokeWidth={1.5} />
                  ) : (
                    <Clock className="size-3.5 text-[hsl(var(--foreground-muted))]" strokeWidth={1.5} />
                  )}
                  <span className={`font-body text-[13px] ${done ? "" : "text-[hsl(var(--foreground-secondary))]"}`}>
                    {sec?.name || slug}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {/* Wave 26.12 — AI Summary panel */}
      {aiSummary && (
        <div className="px-5 pb-4 pt-3 border-t border-[rgba(0,0,0,0.05)] bg-[hsl(var(--accent-gold))]/[0.04]" data-testid={`packet-ai-summary-${packet.id}`}>
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="size-3.5 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
            <Heading level="eyebrow">AI summary</Heading>
            {isPostTrip && <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))]">Post-trip feedback</span>}
          </div>
          {typeof aiSummary === "string" ? (
            <p className="font-accent italic text-[13px] leading-relaxed text-[hsl(var(--foreground))] whitespace-pre-line">
              {aiSummary}
            </p>
          ) : (
            <div className="space-y-2 text-[13px]">
              {aiSummary.highlights && (
                <div>
                  <span className="font-mono text-[9.5px] uppercase tracking-[0.2em] text-[hsl(var(--foreground-muted))]">Highlights</span>
                  <p className="font-accent italic text-[hsl(var(--foreground))] mt-1">{aiSummary.highlights}</p>
                </div>
              )}
              {aiSummary.concerns && (
                <div>
                  <span className="font-mono text-[9.5px] uppercase tracking-[0.2em] text-[hsl(var(--foreground-muted))]">Concerns</span>
                  <p className="font-accent italic text-[hsl(var(--foreground))] mt-1">{aiSummary.concerns}</p>
                </div>
              )}
              {aiSummary.preferences && (
                <div>
                  <span className="font-mono text-[9.5px] uppercase tracking-[0.2em] text-[hsl(var(--foreground-muted))]">Preferences for next time</span>
                  <p className="font-accent italic text-[hsl(var(--foreground))] mt-1">{aiSummary.preferences}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      <div className="border-t border-[rgba(0,0,0,0.05)] px-5 py-2.5 flex items-center justify-end gap-1 flex-wrap">
        {canSummarize && (
          <PortalButton
            variant="ghost"
            size="sm"
            icon={summarizing ? <Loader2 className="size-3.5 animate-spin" /> : <Wand2 className="size-3.5" />}
            onClick={summarize}
            disabled={summarizing}
            data-testid={`packet-summarize-${packet.id}`}
            title="AI-summarize completed sections"
          >
            {summarizing ? "Composing…" : aiSummary ? "Re-summarize" : "AI summary"}
          </PortalButton>
        )}
        {packet.status !== "cancelled" && packet.url && (
          <PortalButton
            variant="ghost"
            size="sm"
            icon={<Copy className="size-3.5" />}
            onClick={copyLink}
            data-testid={`packet-copy-${packet.id}`}
          >
            Copy link
          </PortalButton>
        )}
        {missingCount > 0 && completed.size > 0 && packet.status !== "cancelled" && (
          <PortalButton
            variant="ghost"
            size="sm"
            icon={busyId === `${packet.id}-resend-missing` ? <Loader2 className="size-3.5 animate-spin" /> : <Layers className="size-3.5" />}
            onClick={() => onAction(packet, "resend-missing")}
            disabled={!!busyId}
            data-testid={`packet-resend-missing-${packet.id}`}
            title={`Send a new link with only the ${missingCount} unfinished section${missingCount === 1 ? "" : "s"}`}
          >
            Resend missing ({missingCount})
          </PortalButton>
        )}
        {(packet.status === "sent" || packet.status === "in_progress" || packet.status === "expired") && (
          <PortalButton
            variant="ghost"
            size="sm"
            icon={busyId === `${packet.id}-resend` ? <Loader2 className="size-3.5 animate-spin" /> : <RefreshCw className="size-3.5" />}
            onClick={() => onAction(packet, "resend")}
            disabled={!!busyId}
            data-testid={`packet-resend-${packet.id}`}
          >
            Resend
          </PortalButton>
        )}
        {packet.status !== "completed" && packet.status !== "cancelled" && (
          <PortalButton
            variant="ghost"
            size="sm"
            icon={busyId === `${packet.id}-cancel` ? <Loader2 className="size-3.5 animate-spin" /> : <XCircle className="size-3.5" />}
            onClick={() => onAction(packet, "cancel")}
            disabled={!!busyId}
            data-testid={`packet-cancel-${packet.id}`}
          >
            Cancel
          </PortalButton>
        )}
      </div>
    </Surface>
  );
}

export function ClientFormsPanel({ clientId }) {
  const [packets, setPackets] = React.useState(null);
  const [sections, setSections] = React.useState([]);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [sentToast, setSentToast] = React.useState(null);
  const [busyId, setBusyId] = React.useState(null);

  const refresh = React.useCallback(() => {
    api
      .get("/forms/packets", { params: { client_id: clientId } })
      .then(({ data }) => setPackets(Array.isArray(data) ? data : []))
      .catch(() => setPackets([]));
  }, [clientId]);

  React.useEffect(() => {
    refresh();
    api.get("/forms/sections").then(({ data }) => setSections(Array.isArray(data) ? data : []));
  }, [refresh]);

  const sectionsByslug = React.useMemo(
    () => Object.fromEntries(sections.map((s) => [s.slug, s])),
    [sections],
  );

  const handleSent = (packet) => {
    setDrawerOpen(false);
    setSentToast(packet);
    refresh();
  };

  const handleAction = async (packet, action) => {
    setBusyId(`${packet.id}-${action}`);
    try {
      if (action === "resend") {
        await api.post(`/forms/packets/${packet.id}/resend`, { extend_days: 14 });
      } else if (action === "resend-missing") {
        const { data: newPacket } = await api.post(
          `/forms/packets/${packet.id}/resend-missing`,
          { expires_in_days: 14 },
        );
        // Surface new magic link via the same toast surface
        setSentToast(newPacket);
      } else if (action === "cancel") {
        if (!window.confirm("Cancel this onboarding link? The client will no longer be able to use it.")) {
          return;
        }
        await api.delete(`/forms/packets/${packet.id}`);
      }
      refresh();
    } catch (e) {
      window.alert(`Action failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  if (packets === null) return <Skeleton className="h-40" />;

  return (
    <div className="space-y-8" data-testid="client-forms-panel">
      <StatusBoard packets={packets} />

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Heading level="eyebrow">
          {packets.length} packet{packets.length === 1 ? "" : "s"} on file
        </Heading>
        <div className="flex items-center gap-1.5 flex-wrap">
          <PortalButton
            variant="ghost"
            size="sm"
            icon={busyId === "post-trip" ? <Loader2 className="size-3.5 animate-spin" /> : <Sparkles className="size-3.5" />}
            onClick={async () => {
              if (!window.confirm("Send a post-trip feedback link to this client?")) return;
              setBusyId("post-trip");
              try {
                const { data } = await api.post(`/forms/packets/post-trip-feedback`, { client_id: clientId });
                setSentToast(data?.packet || data);
                refresh();
              } catch (e) {
                window.alert(e?.response?.data?.detail || "Couldn't send post-trip feedback.");
              } finally { setBusyId(null); }
            }}
            disabled={!!busyId}
            data-testid="forms-send-post-trip-btn"
          >
            Send post-trip feedback
          </PortalButton>
          <PortalButton
            variant="primary"
            size="sm"
            icon={<Plus className="size-3.5" />}
            onClick={() => setDrawerOpen(true)}
            data-testid="forms-send-btn"
          >
            Send onboarding
          </PortalButton>
        </div>
      </div>

      {packets.length === 0 ? (
        <Empty
          title="No onboarding sent yet"
          hint="Send the Agreement bundle to start, then follow up with the full intake."
          icon={<ScrollText className="size-6" strokeWidth={1.25} />}
          action={
            <PortalButton variant="ghost" size="sm" icon={<Plus className="size-3.5" />} onClick={() => setDrawerOpen(true)} data-testid="forms-send-first-btn">
              First packet
            </PortalButton>
          }
        />
      ) : (
        <ul className="space-y-4">
          {packets.map((p) => (
            <li key={p.id}>
              <PacketCard packet={p} sectionsByslug={sectionsByslug} onAction={handleAction} busyId={busyId} />
            </li>
          ))}
        </ul>
      )}

      {drawerOpen && (
        <SendPacketDrawer
          clientId={clientId}
          sections={sections}
          onClose={() => setDrawerOpen(false)}
          onSent={handleSent}
        />
      )}

      {sentToast && <PacketSentToast packet={sentToast} onClose={() => setSentToast(null)} />}
    </div>
  );
}
